<i18n
  src="@/locales/views/organization/partners/producers/edit_producer.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="org_partners_producers_show_producer_path"
    :params="{
      organization_id: organization_id,
      producer_id: producer_id
    }"
  />
  <bread-crumbs :labels="[module_name]"></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-6" v-if="producer">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.organization_producer.producer_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="producer.organization_producer.producer_code"
                  :class="[
                    'input-text',
                    isInvalid('organization_producer.producer_code')
                  ]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.producer_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="producer.producer_code"
                  :class="['input-text', isInvalid('producer_code')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.lastname") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="producer.lastname"
                  :class="['input-text', isInvalid('lastname')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.name") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="producer.name"
                  :class="['input-text', isInvalid('name')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.gender_id") }}
              </label>
              <div class="mt-3">
                <radio-form
                  v-model="producer.gender_id"
                  name="gender_id"
                  :options="genders"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.birth_date") }}
              </label>
              <div class="mt-3">
                <date-form v-model="producer.birth_date" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-3">
                <switch-form
                  v-model="producer.organization_producer.active"
                  :label="$t('model.producer.organization_producer.active')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-3">
                <switch-form
                  v-model="producer.organization_producer.formalized"
                  :label="$t('model.producer.organization_producer.formalized')"
                />
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <template
              v-for="(attribute, index) in attribute_producers"
              :key="attribute.id"
            >
              <div
                v-if="isBoolean(attribute.datatype_id)"
                class="sm:col-span-3 flex align-middle items-center"
              >
                <switch-form
                  v-model="producer_attributes[index].value"
                  :label="attribute.label"
                />
              </div>
              <div v-if="isString(attribute.datatype_id)" class="sm:col-span-3">
                <label class="label-form">
                  {{ attribute.label }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="producer_attributes[index].value"
                    :class="['input-text', isInvalid('')]"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowProducer()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="update()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/partners/producers/manage_producer.js";
import GenderMixin from "@/components/views/mixins/organization/genders.js";
import DatatypeMixin from "@/components/views/mixins/organization/datatypes.js";

export default {
  name: "OrgPartnersProducersNewProducer",
  mixins: [GenderMixin, DatatypeMixin],
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      producer: null,
      attribute_producers: [],
      producer_attributes: []
    };
  },
  props: {
    organization_id: {},
    producer_id: {}
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => {
        this.loadData();
      })
    );
  },
  methods: {
    loadData: async function () {
      await this.fetchAttributeProducers();
      await this.fetchProducer();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await Requester.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
      this.producer_crops = this.producer.producer_crops.map(
        prod_crop => prod_crop.crop_id
      );

      this.producer_attributes = this.attribute_producers.map(attr => {
        let producer_attribute = this.producer.producer_attributes.find(
          prod_attr => prod_attr.attribute_producer_id == attr.id
        );
        return {
          id: producer_attribute?.id,
          value: this.datatypeDefaultValue(producer_attribute?.value, attr),
          attribute_producer_id: attr.id,
          organization_id: this.organization_id
        };
      });
    },
    fetchAttributeProducers: async function () {
      let response = await Requester.fetchAttributeProducers(
        this.organization_id
      );

      this.attribute_producers = response.data;
    },
    datatypeDefaultValue: function (producer_attribute_value, attribute) {
      if (this.isBoolean(attribute.datatype_id))
        return Boolean(Number(producer_attribute_value));
      if (this.isString(attribute.datatype_id)) return producer_attribute_value;
    },
    update: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.updateProducer(
          this.organization_id,
          this.producer_id,
          {
            producer_code: this.producer.producer_code,
            name: this.producer.name,
            lastname: this.producer.lastname,
            gender_id: this.producer.gender_id,
            birth_date: this.producer.birth_date,
            organization_producer_attributes:
              this.producer.organization_producer,
            producer_attributes: this.producer_attributes
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowProducer: function () {
      this.$router.push({
        name: "org_partners_producers_show_producer_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id
        }
      });
    }
  }
};
</script>
